import { DateTime } from "luxon";
import { z } from "zod/v4";
import { PatientsIdSchema } from "../zodSchemas.js";
export const QuotesIdSchema = z
    .string()
    .min(7)
    .max(36)
    .transform((value) => value);
export const PecNetworkSchema = z.enum(["santeclair", "itelis"]);
export const PecOcrStatusSchema = z.enum([
    "pending",
    "processing",
    "completed",
    "failed",
    "not_applicable",
]);
export const PecCaptureIdSchema = z.string().uuid();
export const PecDocumentTypeSchema = z.enum([
    "coverage_response",
    "coverage_request",
]);
export function isPecOcrApplicable(documentType) {
    return documentType === "coverage_response";
}
export const PecDocumentUploadInputSchema = z
    .object({
    patientId: PatientsIdSchema.nullable(),
    quoteId: QuotesIdSchema.nullable(),
    documentType: PecDocumentTypeSchema.nullable(),
    network: PecNetworkSchema,
    capturedAt: z.string().datetime({ offset: true }),
    captureId: PecCaptureIdSchema,
})
    .refine((input) => input.patientId !== null || input.quoteId === null, {
    message: "A quote requires a patient",
    path: ["quoteId"],
});
export const PecDocumentUploadResponseSchema = z.object({
    duplicate: z.boolean(),
    patientMediaId: z.string().uuid().nullable(),
    pecDocumentId: z.string().uuid(),
    ocrStatus: PecOcrStatusSchema,
});
const nullableTrimmedStringSchema = z.string().trim().min(1).nullable();
const frenchDateSchema = z
    .string()
    .trim()
    .refine((value) => DateTime.fromFormat(value, "dd/MM/yyyy").isValid, {
    message: "Expected date in dd/MM/yyyy format",
});
const nullableFrenchDateSchema = frenchDateSchema.nullable();
const amountCentsSchema = z.number().int().nonnegative().nullable();
export const PecDocumentsIdSchema = z
    .string()
    .uuid()
    .transform((value) => value);
export const PecOcrDataSchema = z.object({
    totalPecCents: amountCentsSchema,
    refundMutualCents: amountCentsSchema,
    refundSsCents: amountCentsSchema,
    patientResponsibilityCents: amountCentsSchema,
    patientLastName: nullableTrimmedStringSchema,
    patientFirstName: nullableTrimmedStringSchema,
    patientBirthdate: nullableFrenchDateSchema,
    pecDate: nullableFrenchDateSchema,
    pecNumber: nullableTrimmedStringSchema,
    peremptionDate: nullableFrenchDateSchema,
});
export const PecCoverageDecisionSchema = z.enum([
    "approved",
    "partial",
    "refused",
]);
export const pecCoverageDecisionLabels = {
    approved: "Accordée",
    partial: "Accord partiel",
    refused: "Refusée",
};
export const PecCoverageReviewSchema = z.object({
    decision: PecCoverageDecisionSchema.nullable(),
    reviewedAt: z.iso.datetime(),
    reviewer: z.string().min(1),
});
export const PecCoverageReviewsSchema = z.array(PecCoverageReviewSchema);
export const ReviewPecCoverageInputSchema = z.object({
    id: PecDocumentsIdSchema,
    decision: PecCoverageDecisionSchema.nullable(),
    expectedReviewCount: z.number().int().nonnegative(),
});
export const PecDocumentQuoteLinkInputSchema = z.object({
    id: PecDocumentsIdSchema,
    quoteId: QuotesIdSchema.nullable(),
    expectedQuoteId: QuotesIdSchema.nullable(),
});
export const PecDocumentListItemSchema = z.object({
    id: PecDocumentsIdSchema,
    patientId: PatientsIdSchema.nullable(),
    patientMediaId: z.string().uuid().nullable(),
    quoteId: QuotesIdSchema.nullable(),
    documentType: PecDocumentTypeSchema.nullable(),
    network: PecNetworkSchema,
    capturedAt: z.string().datetime({ offset: true }),
    ocrStatus: PecOcrStatusSchema,
    ocrError: z.string().nullable(),
    patientLastName: z.string().nullable(),
    patientFirstName: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    ocrData: PecOcrDataSchema.optional(),
    coverageReviews: PecCoverageReviewsSchema.default([]),
});
export const PecDocumentDetailSchema = z.object({
    id: PecDocumentsIdSchema,
    owner: z.string().min(1),
    patientId: PatientsIdSchema.nullable(),
    patientMediaId: z.string().uuid().nullable(),
    captureId: PecCaptureIdSchema,
    quoteId: QuotesIdSchema.nullable(),
    documentType: PecDocumentTypeSchema.nullable(),
    network: PecNetworkSchema,
    capturedAt: z.string().datetime({ offset: true }),
    ocrStatus: PecOcrStatusSchema,
    ocrError: z.string().nullable(),
    ocrCompletedAt: z.string().datetime({ offset: true }).nullable(),
    stagingS3Key: z.string().nullable(),
    mimeType: z.string().nullable(),
    originalFilename: z.string().nullable(),
    originalFileSizeBytes: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    ocrData: PecOcrDataSchema,
    coverageReviews: PecCoverageReviewsSchema.default([]),
});
export const PecDocumentListInputSchema = z.object({
    patientId: PatientsIdSchema.nullable().optional(),
    stagingOnly: z.boolean().optional(),
    status: PecOcrStatusSchema.nullable().optional(),
});
export const PecDocumentFieldsUpdateInputSchema = z.object({
    id: PecDocumentsIdSchema,
    fields: PecOcrDataSchema,
});
export const PecDocumentAssignInputSchema = z.object({
    id: PecDocumentsIdSchema,
    patientId: PatientsIdSchema,
    quoteId: QuotesIdSchema.nullable(),
    documentType: PecDocumentTypeSchema.nullable(),
});
export const PecDocumentReanalyzeInputSchema = z.object({
    id: PecDocumentsIdSchema,
});
//# sourceMappingURL=PecDocumentsSchemas.js.map